import { defined } from "@glideapps/ts-necessities";
import { setUnionInto } from "collection-utils";
import type { GroundValue } from "../common/computation-model/data";
import { makeRootPath } from "../common/computation-model/paths";
import type { Dirt, Handler, IncomingSlot, Namespace } from "../common/computation-model/types";
import { areSetsOverlapping } from "../support";

export interface RowSubscriptionSources {
    readonly globalKeys: Set<string>;
    // root key -> row ID -> column names
    // row ID === `true` means any row counts
    readonly columnsInRows: ReadonlyMap<string, ReadonlyMap<string | true, ReadonlySet<string>>>;
}

export interface SubscriptionHandler {
    readonly needsRedraw: boolean;
}

abstract class SubscriptionHandlerBase implements Handler, SubscriptionHandler {
    public readonly isDirty = false;
    public needsRedraw = false;
    public abstract readonly symbolicRepresentation: string;

    constructor(private readonly slots: IncomingSlot[], private readonly onChange: () => void) {}

    public dependenciesAreDirty(): void {
        this.onChange();
    }

    public getSlots(): readonly IncomingSlot[] {
        return this.slots;
    }

    public recompute(_ns: Namespace): GroundValue {
        return undefined;
    }

    public setDirty(): void {
        return;
    }
}

export class RowSubscriptionHandler extends SubscriptionHandlerBase {
    public readonly symbolicRepresentation: string = "row-subscription";

    constructor(sources: RowSubscriptionSources, onChange: () => void) {
        super(
            [
                ...Array.from(sources.globalKeys).map(k => ({
                    sourcePath: makeRootPath(k),
                    process: () => {
                        if (this.needsRedraw) return false;
                        this.needsRedraw = true;
                        onChange();
                        return true;
                    },
                })),
                ...Array.from(sources.columnsInRows).map(([k, m]) => {
                    let allColumns: Set<string> | undefined;
                    return {
                        sourcePath: makeRootPath(k),
                        process: (d: Dirt) => {
                            if (this.needsRedraw) return false;
                            let rowColumns: ReadonlySet<string>;
                            if (d.kind === "row") {
                                if (m.has(d.rowID)) {
                                    rowColumns = defined(m.get(d.rowID));
                                } else if (m.has(true)) {
                                    rowColumns = defined(m.get(true));
                                } else {
                                    return false;
                                }
                            } else {
                                if (allColumns === undefined) {
                                    allColumns = new Set();
                                    for (const columns of m.values()) {
                                        setUnionInto(allColumns, columns);
                                    }
                                }
                                rowColumns = allColumns;
                            }
                            if (d.columns === true || areSetsOverlapping(d.columns, rowColumns)) {
                                this.needsRedraw = true;
                                onChange();
                                return true;
                            }
                            return false;
                        },
                    };
                }),
            ],
            onChange
        );
    }
}

export interface TableSubscriptionSources {
    readonly globalKeys: Set<string>;
    // key -> column names
    readonly columnsInDirectRows: ReadonlyMap<string, ReadonlySet<string>>;
    readonly columnsInIndirectRows: ReadonlyMap<string, ReadonlySet<string>>;
}

export class TableSubscriptionHandler extends SubscriptionHandlerBase {
    // `true` means all rows
    public rowsNeedRedraw: Set<string> | true = new Set();
    public readonly symbolicRepresentation: string = "table-subscription";

    constructor(sources: TableSubscriptionSources, onChange: () => void) {
        super(
            [
                ...Array.from(sources.globalKeys).map(k => ({
                    sourcePath: makeRootPath(k),
                    process: () => {
                        if (this.needsRedraw && this.rowsNeedRedraw === true) return false;
                        this.needsRedraw = true;
                        this.rowsNeedRedraw = true;
                        onChange();
                        return true;
                    },
                })),
                ...Array.from(sources.columnsInDirectRows).map(([k, cs]) => ({
                    sourcePath: makeRootPath(k),
                    process: (d: Dirt) => {
                        if (this.rowsNeedRedraw === true) return false;
                        if (d.columns === true || areSetsOverlapping(d.columns, cs)) {
                            if (d.kind === "row") {
                                this.rowsNeedRedraw.add(d.rowID);
                            } else {
                                this.rowsNeedRedraw = true;
                            }
                            this.needsRedraw = true;
                            onChange();
                            return true;
                        }
                        return false;
                    },
                })),
                ...Array.from(sources.columnsInIndirectRows).map(([k, cs]) => ({
                    sourcePath: makeRootPath(k),
                    process: (d: Dirt) => {
                        if (this.needsRedraw && this.rowsNeedRedraw === true) return false;
                        if (d.columns === true || areSetsOverlapping(d.columns, cs)) {
                            this.rowsNeedRedraw = true;
                            this.needsRedraw = true;
                            onChange();
                            return true;
                        }
                        return false;
                    },
                })),
            ],
            onChange
        );
    }
}
